import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * AssessmentResults - Public page to display assessment results via magic link
 *
 * This page is accessible without authentication - the token in the URL
 * serves as the authentication mechanism.
 */
function AssessmentResults() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/results/${token}`);
        const data = await response.json();

        if (!response.ok) {
          if (data.expired) {
            setError({
              type: 'expired',
              message: data.message || 'This link has expired.',
            });
          } else {
            setError({
              type: 'error',
              message: data.error || 'Unable to load results.',
            });
          }
          return;
        }

        setResults(data);
      } catch (err) {
        setError({
          type: 'error',
          message: 'Failed to connect to the server. Please try again later.',
        });
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchResults();
    } else {
      setLoading(false);
      setError({ type: 'error', message: 'Invalid link.' });
    }
  }, [token]);

  if (loading) {
    return (
      <div className="assessment-results-page">
        <div className="results-container loading">
          <div className="loading-spinner"></div>
          <p>Loading your results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assessment-results-page">
        <div className="results-container error">
          <div className="error-icon">
            {error.type === 'expired' ? '⏰' : '⚠️'}
          </div>
          <h1>{error.type === 'expired' ? 'Link Expired' : 'Unable to Load Results'}</h1>
          <p>{error.message}</p>
          {error.type === 'expired' && (
            <div className="resend-section">
              <p>Need a new link? Enter your email to receive a fresh one.</p>
              <ResendLinkForm />
            </div>
          )}
        </div>
      </div>
    );
  }

  const { student_name, learning_style, subject_assessment, submitted_at, expires_at } = results;

  return (
    <div className="assessment-results-page">
      <div className="results-container">
        <header className="results-header">
          <img src="/lrbrain-logo.png" alt="L.R. Brain Solutions" className="results-logo" />
          <h1>Your Learning Assessment Results</h1>
          {student_name && <p className="student-name">Hello, {student_name}!</p>}
          {submitted_at && (
            <p className="submitted-date">
              Assessment completed: {new Date(submitted_at).toLocaleDateString()}
            </p>
          )}
        </header>

        {/* Learning Style Section */}
        {learning_style && !learning_style.error && (
          <section className="results-section learning-style">
            <h2>Your Learning Style Profile</h2>

            <div className="style-summary">
              <div className="primary-style">
                <span className="label">Primary Style</span>
                <span className="value">{formatStyle(learning_style.primary_style)}</span>
              </div>
              {learning_style.secondary_style && (
                <div className="secondary-style">
                  <span className="label">Secondary Style</span>
                  <span className="value">{formatStyle(learning_style.secondary_style)}</span>
                </div>
              )}
            </div>

            {learning_style.style_percentages && (
              <div className="style-breakdown">
                <h3>Style Breakdown</h3>
                <div className="style-bars">
                  {Object.entries(learning_style.style_percentages).map(([style, percentage]) => (
                    <div key={style} className="style-bar">
                      <span className="style-name">{formatStyle(style)}</span>
                      <div className="bar-container">
                        <div
                          className="bar-fill"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="percentage">{percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {learning_style.summary && (
              <div className="style-description">
                <p>{learning_style.summary}</p>
              </div>
            )}

            {learning_style.study_recommendations && (
              <div className="recommendations">
                <h3>Study Recommendations</h3>
                <ul>
                  {learning_style.study_recommendations.map((rec, i) => (
                    <li key={i}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}

            {learning_style.environment_preferences && (
              <div className="environment">
                <h3>Ideal Study Environment</h3>
                <ul>
                  {learning_style.environment_preferences.map((pref, i) => (
                    <li key={i}>{pref}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="additional-info">
              {learning_style.collaboration_style && (
                <div className="info-item">
                  <span className="label">Collaboration Style:</span>
                  <span className="value">{formatStyle(learning_style.collaboration_style)}</span>
                </div>
              )}
              {learning_style.attention_span_profile && (
                <div className="info-item">
                  <span className="label">Attention Profile:</span>
                  <span className="value">{formatStyle(learning_style.attention_span_profile)}</span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Subject Assessment Section */}
        {subject_assessment && !subject_assessment.error && (
          <section className="results-section subject-assessment">
            <h2>Subject Assessment</h2>

            <div className="overall-level">
              <span className="label">Overall Academic Level</span>
              <span className={`value level-${subject_assessment.overall_academic_level}`}>
                {formatLevel(subject_assessment.overall_academic_level)}
              </span>
            </div>

            {subject_assessment.summary && (
              <div className="assessment-description">
                <p>{subject_assessment.summary}</p>
              </div>
            )}

            {subject_assessment.subjects && subject_assessment.subjects.length > 0 && (
              <div className="subjects-grid">
                {subject_assessment.subjects.map((subject, i) => (
                  <div key={i} className="subject-card">
                    <div className="subject-header">
                      <h3>{subject.subject}</h3>
                      <span className={`skill-level level-${subject.skill_level}`}>
                        {formatLevel(subject.skill_level)}
                      </span>
                    </div>
                    <div className="confidence">
                      <span className="label">Confidence:</span>
                      <div className="confidence-bar">
                        <div
                          className="confidence-fill"
                          style={{ width: `${subject.confidence_score}%` }}
                        ></div>
                      </div>
                      <span className="percentage">{subject.confidence_score}%</span>
                    </div>
                    {subject.strengths && subject.strengths.length > 0 && (
                      <div className="strengths">
                        <h4>Strengths</h4>
                        <ul>
                          {subject.strengths.map((s, j) => (
                            <li key={j}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {subject.areas_for_growth && subject.areas_for_growth.length > 0 && (
                      <div className="growth-areas">
                        <h4>Areas for Growth</h4>
                        <ul>
                          {subject.areas_for_growth.map((a, j) => (
                            <li key={j}>{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {subject.recommended_focus && (
                      <div className="focus">
                        <h4>Recommended Focus</h4>
                        <p>{subject.recommended_focus}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {subject_assessment.priority_subjects && subject_assessment.priority_subjects.length > 0 && (
              <div className="priority-subjects">
                <h3>Priority Areas</h3>
                <div className="priority-tags">
                  {subject_assessment.priority_subjects.map((subj, i) => (
                    <span key={i} className="priority-tag">{subj}</span>
                  ))}
                </div>
              </div>
            )}

            {subject_assessment.recommended_starting_point && (
              <div className="starting-point">
                <h3>Recommended Starting Point</h3>
                <p>{subject_assessment.recommended_starting_point}</p>
              </div>
            )}

            {subject_assessment.estimated_tutoring_hours && (
              <div className="tutoring-hours">
                <span className="label">Recommended Weekly Tutoring:</span>
                <span className="value">{formatTutoringHours(subject_assessment.estimated_tutoring_hours)}</span>
              </div>
            )}
          </section>
        )}

        {/* Next Steps */}
        {results.next_steps && results.next_steps.length > 0 && (
          <section className="results-section next-steps">
            <h2>Your Next Steps</h2>
            <ol>
              {results.next_steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </section>
        )}

        {/* Overall Summary */}
        {results.overall_summary && (
          <section className="results-section overall-summary">
            <h2>Summary</h2>
            <p>{results.overall_summary}</p>
          </section>
        )}

        {/* Footer */}
        <footer className="results-footer">
          <p>
            Questions about your results?{' '}
            <a href="mailto:support@lrbrainsolutions.com">Contact us</a>
          </p>
          {expires_at && (
            <p className="expiry-notice">
              This link will expire on {new Date(expires_at).toLocaleString()}
            </p>
          )}
          <p className="print-notice">
            <button onClick={() => window.print()} className="print-button">
              Print or Save as PDF
            </button>
          </p>
        </footer>
      </div>

      <style>{`
        .assessment-results-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 2rem;
        }

        .results-container {
          max-width: 900px;
          margin: 0 auto;
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          overflow: hidden;
        }

        .results-container.loading,
        .results-container.error {
          padding: 4rem 2rem;
          text-align: center;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
        }

        .results-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 2rem;
          text-align: center;
        }

        .results-logo {
          height: 60px;
          margin-bottom: 1rem;
        }

        .results-header h1 {
          margin: 0 0 0.5rem;
          font-size: 1.75rem;
        }

        .student-name {
          font-size: 1.25rem;
          margin: 0.5rem 0;
          opacity: 0.9;
        }

        .submitted-date {
          font-size: 0.9rem;
          opacity: 0.7;
          margin: 0;
        }

        .results-section {
          padding: 2rem;
          border-bottom: 1px solid #eee;
        }

        .results-section:last-of-type {
          border-bottom: none;
        }

        .results-section h2 {
          color: #333;
          margin: 0 0 1.5rem;
          font-size: 1.5rem;
          padding-bottom: 0.5rem;
          border-bottom: 3px solid #667eea;
          display: inline-block;
        }

        .results-section h3 {
          color: #555;
          margin: 1.5rem 0 1rem;
          font-size: 1.1rem;
        }

        .style-summary {
          display: flex;
          gap: 2rem;
          margin-bottom: 1.5rem;
        }

        .primary-style,
        .secondary-style {
          flex: 1;
          background: #f8f9fa;
          padding: 1rem;
          border-radius: 8px;
          text-align: center;
        }

        .primary-style .value {
          display: block;
          font-size: 1.5rem;
          font-weight: 600;
          color: #667eea;
          margin-top: 0.5rem;
        }

        .secondary-style .value {
          display: block;
          font-size: 1.25rem;
          font-weight: 500;
          color: #764ba2;
          margin-top: 0.5rem;
        }

        .style-bar {
          display: flex;
          align-items: center;
          margin-bottom: 0.75rem;
          gap: 1rem;
        }

        .style-name {
          width: 120px;
          font-size: 0.9rem;
        }

        .bar-container {
          flex: 1;
          height: 24px;
          background: #eee;
          border-radius: 12px;
          overflow: hidden;
        }

        .bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #667eea, #764ba2);
          border-radius: 12px;
          transition: width 0.5s ease;
        }

        .percentage {
          width: 50px;
          text-align: right;
          font-weight: 600;
          color: #667eea;
        }

        .style-description,
        .assessment-description {
          background: #f8f9fa;
          padding: 1rem;
          border-radius: 8px;
          font-style: italic;
          color: #555;
          margin: 1rem 0;
        }

        .recommendations ul,
        .environment ul,
        .strengths ul,
        .growth-areas ul {
          margin: 0;
          padding-left: 1.5rem;
        }

        .recommendations li,
        .environment li,
        .strengths li,
        .growth-areas li {
          margin-bottom: 0.5rem;
          color: #555;
        }

        .additional-info {
          display: flex;
          gap: 2rem;
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid #eee;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .info-item .label {
          font-size: 0.85rem;
          color: #888;
        }

        .info-item .value {
          font-weight: 500;
          color: #333;
        }

        .overall-level {
          text-align: center;
          padding: 1.5rem;
          background: #f8f9fa;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .overall-level .label {
          display: block;
          font-size: 0.9rem;
          color: #888;
          margin-bottom: 0.5rem;
        }

        .overall-level .value {
          font-size: 1.5rem;
          font-weight: 600;
        }

        .level-beginner { color: #e74c3c; }
        .level-developing { color: #f39c12; }
        .level-proficient { color: #27ae60; }
        .level-advanced { color: #2980b9; }
        .level-expert { color: #8e44ad; }
        .level-below_grade { color: #e74c3c; }
        .level-at_grade { color: #27ae60; }
        .level-above_grade { color: #2980b9; }

        .subjects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
        }

        .subject-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 1.5rem;
        }

        .subject-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .subject-header h3 {
          margin: 0;
          font-size: 1.1rem;
        }

        .skill-level {
          font-size: 0.85rem;
          font-weight: 600;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          background: white;
        }

        .confidence {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .confidence .label {
          font-size: 0.8rem;
          color: #888;
        }

        .confidence-bar {
          flex: 1;
          height: 8px;
          background: #ddd;
          border-radius: 4px;
          overflow: hidden;
        }

        .confidence-fill {
          height: 100%;
          background: #27ae60;
          border-radius: 4px;
        }

        .confidence .percentage {
          font-size: 0.85rem;
          width: auto;
        }

        .subject-card h4 {
          margin: 1rem 0 0.5rem;
          font-size: 0.9rem;
          color: #555;
        }

        .subject-card ul {
          margin: 0;
          padding-left: 1.25rem;
          font-size: 0.9rem;
        }

        .subject-card li {
          margin-bottom: 0.25rem;
        }

        .focus p {
          margin: 0;
          font-size: 0.9rem;
          color: #667eea;
          font-weight: 500;
        }

        .priority-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .priority-tag {
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.9rem;
        }

        .starting-point {
          background: #e8f4f8;
          padding: 1rem;
          border-radius: 8px;
          border-left: 4px solid #667eea;
        }

        .starting-point h3 {
          margin-top: 0;
        }

        .starting-point p {
          margin: 0;
        }

        .tutoring-hours {
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid #eee;
        }

        .tutoring-hours .value {
          font-weight: 600;
          color: #667eea;
          margin-left: 0.5rem;
        }

        .next-steps ol {
          margin: 0;
          padding-left: 1.5rem;
        }

        .next-steps li {
          margin-bottom: 0.75rem;
          color: #444;
          line-height: 1.5;
        }

        .overall-summary p {
          font-size: 1.1rem;
          line-height: 1.6;
          color: #444;
        }

        .results-footer {
          background: #f8f9fa;
          padding: 1.5rem 2rem;
          text-align: center;
          color: #666;
        }

        .results-footer a {
          color: #667eea;
        }

        .expiry-notice {
          font-size: 0.85rem;
          opacity: 0.7;
        }

        .print-button {
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1rem;
          margin-top: 1rem;
        }

        .print-button:hover {
          opacity: 0.9;
        }

        .resend-section {
          margin-top: 2rem;
        }

        @media print {
          .assessment-results-page {
            background: white;
            padding: 0;
          }

          .results-container {
            box-shadow: none;
          }

          .print-button,
          .results-footer {
            display: none;
          }
        }

        @media (max-width: 600px) {
          .assessment-results-page {
            padding: 1rem;
          }

          .results-section {
            padding: 1.5rem;
          }

          .style-summary {
            flex-direction: column;
            gap: 1rem;
          }

          .additional-info {
            flex-direction: column;
            gap: 1rem;
          }

          .subject-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
}

// Helper Components
function ResendLinkForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/assessment/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: 'success', message: data.message });
        setEmail('');
      } else {
        setStatus({ type: 'error', message: data.error || 'Failed to send link' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Failed to connect to server' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="resend-form">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email"
        required
        disabled={loading}
      />
      <button type="submit" disabled={loading}>
        {loading ? 'Sending...' : 'Send New Link'}
      </button>
      {status && (
        <p className={`status-message ${status.type}`}>
          {status.message}
        </p>
      )}
      <style>{`
        .resend-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          max-width: 300px;
          margin: 1rem auto 0;
        }

        .resend-form input {
          padding: 0.75rem 1rem;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 1rem;
        }

        .resend-form button {
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          border: none;
          padding: 0.75rem;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1rem;
        }

        .resend-form button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .status-message {
          font-size: 0.9rem;
          margin: 0;
        }

        .status-message.success {
          color: #27ae60;
        }

        .status-message.error {
          color: #e74c3c;
        }
      `}</style>
    </form>
  );
}

// Formatting helpers
function formatStyle(style) {
  if (!style) return '';
  return style
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatLevel(level) {
  if (!level) return '';
  return level
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatTutoringHours(hours) {
  const mapping = {
    light: '1-2 hours/week (Light)',
    moderate: '2-4 hours/week (Moderate)',
    intensive: '4+ hours/week (Intensive)',
  };
  return mapping[hours] || hours;
}

export default AssessmentResults;
